/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnDestroy, OnInit } from '@angular/core';
import { REGION_CODE } from '@gh/config';
import { BuilderUserContext } from '@gh/builder-shared';
import {
  BuilderUserProfileValidationDto,
  SystemVarsRestService,
  ValidationStatus,
} from '@gh/builder-shared-data';
import { HelpID } from '@gh/common-shared';
import { Enum } from '@gh/core-data';
import { TranslationService } from '@gh/core-mls';
import { DialogInput, splitByLines, WaitingService } from '@gh/core-ui';
import { of$, timer$ } from '@gh/rx';
import { catchError$, switchMap$, takeWhile$ } from '@gh/rx/operators';
import { fromPairs, isString } from 'lodash';
import { Subscription } from 'rxjs';

export type UserProfileValidationMessage = {
  header: string;
  body?: string;
  footer?: string;
};

@Component({
  standalone: false,
  selector: 'gh-user-profile-validation-dialog',
  templateUrl: 'user-profile-validation-dialog.component.html',
  styleUrls: ['user-profile-validation-dialog.component.scss'],
})
export class UserProfileValidationDialogComponent implements OnInit, OnDestroy, DialogInput<BuilderUserProfileValidationDto> {
  message: UserProfileValidationMessage;
  input: BuilderUserProfileValidationDto;
  helpId: HelpID;
  invoiceLink: string | null = null;
  private pollingSubscription?: Subscription;

  constructor(private userContext: BuilderUserContext,
    private translationService: TranslationService,
    private waitingService: WaitingService,
    private systemVarsRest: SystemVarsRestService) {

  }

  get title(): string {
    return {
      [ValidationStatus.LoginDenied]: 'msg_login_denied',
      [ValidationStatus.SessionWasExpired]: 'msg_session_was_expired',
      [ValidationStatus.OfficeIsNotActivated]: 'lbl_office_not_activated',
      [ValidationStatus.AcceptSubscription]: 'lbl_stripe_activate',
    }[this.input.paymentStatus];
  }



  get hasUnpaidInvoice(): boolean {
    return this.input.paymentStatus === ValidationStatus.AcceptSubscription;
  }

  get paymentButtonTooltip(): string {
    if (!this.invoiceLink) {
      return this.translationService.get('msg_invoice_will_be_available_shortly');
    }
    return '';
  }

  ngOnInit(): void {
    this.message = this.getMessage(Enum.toValue(ValidationStatus, this.input.paymentStatus));

    if (this.needsHelpId()) {
      this.helpId = 'common/first-franchisee-login';
    }

    if (this.hasUnpaidInvoice) {
      this.startPollingInvoiceLink();
    }
  }

  ngOnDestroy(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  onLogout(): void {
    this.userContext.logout();
  }



  makePayment(): void {
    if (this.invoiceLink) {
      window.open(this.invoiceLink, '_blank');
    }
  }

  private startPollingInvoiceLink(): void {
    this.pollingSubscription = timer$(0, 10000).pipe(
      switchMap$(() =>
        this.systemVarsRest.getInvoiceLink().pipe(
          catchError$(() => {
            return of$({ invoiceUrl: null });
          })
        )
      ),
      takeWhile$((response) => !response || !response.invoiceUrl, true)
    ).subscribe((response) => {
      if (response && response.invoiceUrl) {
        this.invoiceLink = response.invoiceUrl;
        if (this.pollingSubscription) {
          this.pollingSubscription.unsubscribe();
          this.pollingSubscription = undefined;
        }
      }
    });
  }

  private getMessage(messageId: string): UserProfileValidationMessage {
    const raw = this.translationService.getRaw(messageId);

    const currencyCode = this.getCurrencyCode();
    const translationParams = {
      ...this.input,
      currencyCode,
    };

    if (isString(raw)) {
      return { header: splitByLines(this.translationService.get(messageId, translationParams)) };
    } else {
      return <UserProfileValidationMessage>fromPairs(['header', 'body', 'footer'].filter((key) => raw[key])
        .map((key) => [key, splitByLines(this.translationService.get(`${messageId}.${key}`, translationParams))]));
    }
  }

  private getCurrencyCode(): string {
    const currencyMap: { [key: string]: string } = {
      'au': 'A$',
      'nz': 'NZ$',
      'us': '$',
    };
    return currencyMap[REGION_CODE] || '$';
  }

  private needsHelpId(): boolean {
    const { paymentStatus } = this.input;
    switch (paymentStatus) {
      case ValidationStatus.LoginDenied:
      case ValidationStatus.SessionWasExpired:
        return false;
      default:
        return true;
    }
  }
}
